import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { selectAllActiveForm, resetState, submitForm, updateActiveFormDraft, resetActiveFormStatus, selectActiveFormExistingOrder } from "../../reducers/active-form-slice";
import { resetAdditionalInformationFiles } from '../../reducers/additional-information-files-slice';
import { resetSWITRSDetailsFiles } from '../../reducers/switrs-details-files-slice';
import { resetTrafficCollisionsReportRetrievalFiles } from '../../reducers/traffic-collisions-report-retrieval-files-slice';
import { resetFaultInfoFilesStatus } from '../../reducers/fault-info-files-slice';
import { resetAttachmentsAfterSubmission } from "../../reducers/attachments-slice";
import ConfirmCancelModal from "../../components/modals/confirm-cancel-order";
import SubmissionFailureModal from '../../components/modals/submission-failure';
import { updateOrder, selectOrder, resetStatus } from '../../reducers/order-details-slice';
import { useBuildUpdatedDraftOrder } from '../../utility/build-updated-draft-order';
import FormService from '../../services/form-service';
import { BeatLoader } from 'react-spinners';


const FormHeaderButtons = ({ updateForm, showSaveForLater=true }) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [submitType, setSubmitType] = useState();
    const order = useSelector(selectOrder);
    const isExistingOrder = useSelector(selectActiveFormExistingOrder);
    const orderStatus = useSelector(state => state.orderDetails.status);
    const { buildUpdatedDraftOrder, uploadAttachments } = useBuildUpdatedDraftOrder();

    /**
     * get current form values
     */
    const allActiveFormValues = useSelector(selectAllActiveForm);
    const activeFormStatus = useSelector(state => state.activeForm.status);
    const activeFormError = useSelector(state => state.activeForm.error);
    const activeFormDraft = useSelector(state => state.activeForm.draft);
    const orderError = useSelector(state => state.orderDetails.error);

    /**
     * handle cancelling
     */
    const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false);

    const handleShowConfirmCancelModal = () => {
        setShowConfirmCancelModal(!showConfirmCancelModal);
    };
    const handleHideConfirmCancelModal = () => {
        setShowConfirmCancelModal(false);
    };

    /**
     * handle save for later
     */
    const handleSaveForLater = async () => {
        await updateForm();

        setSubmitType('saveForLater');
        await dispatch(updateActiveFormDraft(true));
    };


    /**
     * Handle form submission and either submit the form or build out the updated order so that it matches the endpoint's expected order object
     */
    useEffect(() => {
        if (activeFormDraft === true) {
            if (isExistingOrder) {
                const updatedOrder = buildUpdatedDraftOrder();
                (async () => {
                    await dispatch(updateOrder(updatedOrder));
                    await uploadAttachments();
                })();
            } else {
                dispatch(submitForm(allActiveFormValues));
            }
        }
    }, [activeFormDraft]);


    /**
     * Handle SubmissionFailureModal
     */
    const [showSubmissionFailureModal, setShowSubmissionFailureModal] = useState(false);
    const handleHideSubmissionFailureModal = () => {
        setShowSubmissionFailureModal(false);
    };

    const handleShowSubmissionFailureModal = () => {
        setShowSubmissionFailureModal(!showSubmissionFailureModal);
    }

    /**
     * After form submission
     */
    useEffect(() => {
        if ((activeFormStatus === "succeeded" || orderStatus === "succeeded") && showSaveForLater) {
            (async () => {
                await handleResetFormValues();
                if (submitType === "saveForLater") {
                    navigate('/intake/success?draft=true');
                }
            })();
        } else if (activeFormStatus === "failed" || orderStatus === "failed") {
            dispatch(updateActiveFormDraft(false));
            dispatch(resetActiveFormStatus());
            dispatch(resetStatus());
            setShowSubmissionFailureModal(!showSubmissionFailureModal);
        }
    }, [activeFormStatus, orderStatus, submitType, dispatch]);


    // Sending order confirmation email for orders that originally existed as a draft
    useEffect(() => {
        if (submitType === "saveForLater" && orderStatus === "succeeded") {
            (async () => {   
                await FormService.SendOrderConfirmationEmail(true, [allActiveFormValues.generalInformation.emailPrimary], order.details.orderNumber, allActiveFormValues.generalInformation.firstNameCaseManager);
            })();
        }
    }, [orderStatus])


    const handleResetFormValues = async () => {
        await dispatch(resetState());
        await dispatch(resetSWITRSDetailsFiles());
        await dispatch(resetAdditionalInformationFiles());
        await dispatch(resetTrafficCollisionsReportRetrievalFiles());
        await dispatch(resetFaultInfoFilesStatus());
        await dispatch(resetAttachmentsAfterSubmission());
    }; 


    /**
     * handle the next button
     */
    const handleNext = async (e) => {
        e.preventDefault();
        await handleResetFormValues();
        navigate("/portal");
    };


    return (
        <div className="form-header-buttons">
            {(activeFormStatus === "loading" || orderStatus === "loading") && <div className="loader-container"><BeatLoader color="#00818C" /></div>}

            <div className="form-header-buttons-right">
                <button type="button" className="form-cancel" onClick={handleShowConfirmCancelModal}>Cancel Order</button>
                {showSaveForLater && <button type="button" className="form-save-draft" onClick={handleSaveForLater} name="draft" id="draft" value="true">Save For Later</button>}
            </div>

            {showConfirmCancelModal &&
                <ConfirmCancelModal
                    showConfirmCancelModal={showConfirmCancelModal}
                    handleHideConfirmCancelModal={handleHideConfirmCancelModal}
                    handleShowSubmissionFailureModal={handleShowSubmissionFailureModal}
                    handleResetFormValues={handleResetFormValues}
                />
            }

            {showSubmissionFailureModal &&
                <SubmissionFailureModal
                    showSubmissionFailureModal={showSubmissionFailureModal}
                    handleHideSubmissionFailureModal={handleHideSubmissionFailureModal}
                    failureMessage={activeFormError ?? orderError}
                    continueOrder = {handleNext}
                />
            }
        </div>
    );
};

export default FormHeaderButtons;